'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export const DEFAULT_PLATFORM_NAME = 'PRO Flow 360';
export const DEFAULT_COMPANY_NAME = 'Manhatin Computer Software consultancy';
export const DEFAULT_LOGO_URL =
  'https://lvwuhjnquxyyfdlxzqjl.supabase.co/storage/v1/object/public/public-media/platform/settings/logo/1788860304047-Untitled-design-_27_.png';
export const DEFAULT_FAVICON_URL =
  'https://lvwuhjnquxyyfdlxzqjl.supabase.co/storage/v1/object/public/public-media/platform/settings/favicon/1788863277340-PROFlow-360.png';

type PlatformSettingsContextValue = {
  platformName: string;
  companyName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
};

type PlatformSettingsRow = {
  platform_name?: string | null;
  company_name?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
};

type CachedPlatformSettings = Omit<PlatformSettingsContextValue, 'loading' | 'refresh'>;

const DEFAULT_PLATFORM_SETTINGS: PlatformSettingsContextValue = {
  platformName: DEFAULT_PLATFORM_NAME,
  companyName: DEFAULT_COMPANY_NAME,
  logoUrl: DEFAULT_LOGO_URL,
  faviconUrl: DEFAULT_FAVICON_URL,
  loading: true,
  refresh: async () => {},
};

let platformSettingsCache: CachedPlatformSettings | null = null;
let platformSettingsPromise: Promise<CachedPlatformSettings> | null = null;

const PlatformSettingsContext =
  createContext<PlatformSettingsContextValue>(DEFAULT_PLATFORM_SETTINGS);

function normalizePlatformSettings(
  row: PlatformSettingsRow | null | undefined
): CachedPlatformSettings {
  return {
    platformName: String(row?.platform_name || '').trim() || DEFAULT_PLATFORM_NAME,
    companyName: String(row?.company_name || '').trim() || DEFAULT_COMPANY_NAME,
    logoUrl: String(row?.logo_url || '').trim() || DEFAULT_LOGO_URL,
    faviconUrl: String(row?.favicon_url || '').trim() || DEFAULT_FAVICON_URL,
  };
}

async function fetchPlatformSettings(): Promise<CachedPlatformSettings> {
  if (platformSettingsCache) return platformSettingsCache;
  if (platformSettingsPromise) return platformSettingsPromise;

  platformSettingsPromise = (async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('platform_settings')
        .select('platform_name, company_name, logo_url, favicon_url')
        .limit(1)
        .maybeSingle();

      platformSettingsCache = normalizePlatformSettings(
        (data as PlatformSettingsRow | null) || null
      );
      return platformSettingsCache;
    } catch {
      platformSettingsCache = normalizePlatformSettings(null);
      return platformSettingsCache;
    } finally {
      platformSettingsPromise = null;
    }
  })();

  return platformSettingsPromise;
}

export function PlatformSettingsProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PlatformSettingsContextValue>(() => ({
    ...DEFAULT_PLATFORM_SETTINGS,
    ...(platformSettingsCache || {}),
    loading: !platformSettingsCache,
  }));

  const refresh = useCallback(async () => {
    platformSettingsCache = null;
    platformSettingsPromise = null;
    setState((prev) => ({ ...prev, loading: true }));

    const next = await fetchPlatformSettings();
    setState({
      ...next,
      loading: false,
      refresh,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchPlatformSettings().then((next) => {
      if (cancelled) return;
      setState({
        ...next,
        loading: false,
        refresh,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    const handleRefresh = () => {
      void refresh();
    };

    window.addEventListener('platform-settings:refresh', handleRefresh);
    return () => window.removeEventListener('platform-settings:refresh', handleRefresh);
  }, [refresh]);

  useEffect(() => {
    const faviconHref = state.faviconUrl || '/favicon.ico';
    const selectors = [
      'link[rel="icon"]',
      'link[rel="shortcut icon"]',
      'link[rel="apple-touch-icon"]',
    ];

    selectors.forEach((selector) => {
      const link = document.head.querySelector(selector) as HTMLLinkElement | null;
      if (link) {
        link.href = faviconHref;
      }
    });
  }, [state.faviconUrl]);

  const value = useMemo(() => state, [state]);

  return (
    <PlatformSettingsContext.Provider value={value}>{children}</PlatformSettingsContext.Provider>
  );
}

export function usePlatformSettings() {
  return useContext(PlatformSettingsContext);
}

export function resetPlatformSettingsCache() {
  platformSettingsCache = null;
  platformSettingsPromise = null;
}
